import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { postSendResetPasswordCode, ApiRequestError } from '@/api';
import { SubpageHeader } from '@/components/ui/SubpageHeader';

const STORAGE_EMAIL_KEY = 'gf_pwd_reset_email';

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showConsent, setShowConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const email = user?.email?.trim() ?? '';

  const handleDisagree = () => {
    if (sending) return;
    setShowConsent(false);
    setSendError(null);
  };

  const handleAgree = async () => {
    if (!email) {
      setSendError('No email is on file for this account. We cannot send a verification code.');
      return;
    }
    setSendError(null);
    setSending(true);
    try {
      await postSendResetPasswordCode(email);
      sessionStorage.setItem(STORAGE_EMAIL_KEY, email);
      setShowConsent(false);
      navigate('/me/password/verify');
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : 'Failed to send. Please try again later.';
      setSendError(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      <SubpageHeader
        title="Change Password"
        subtitle="Verify by email before updating"
        onBack={() => navigate('/me')}
      />

      <div className="flex-1 px-4 py-8 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-lg bg-amber-50 flex items-center justify-center mb-4">
          <i className="ri-mail-send-line text-amber-500 text-3xl" />
        </div>
        <h2 className="text-gray-900 font-bold text-lg mb-2">Verify by email</h2>
        <p className="text-gray-500 text-xs leading-relaxed max-w-sm mb-6">
          For your security, we will send a verification code to your registered email before you can change your password.
          Tap the button below if you agree to receive that email.
        </p>
        <button
          type="button"
          onClick={() => setShowConsent(true)}
          className="w-full max-w-sm h-8 bg-green-500 hover:bg-green-600 text-white font-medium text-xs rounded-md cursor-pointer transition-colors flex items-center justify-center gap-1.5"
        >
          <i className="ri-shield-check-line text-sm" />
          Change Password
        </button>
      </div>

      {showConsent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={() => !sending && setShowConsent(false)}
        >
          <div className="absolute inset-0 bg-black/40" aria-hidden />
          <div
            className="relative w-full max-w-md mx-auto bg-white rounded-lg border border-gray-200 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-amber-50 mb-3">
                <i className="ri-mail-line text-amber-500 text-lg" />
              </div>
              <div className="text-gray-900 font-semibold text-sm text-center">Send a code to your email?</div>
              <p className="text-left text-gray-500 text-xs mt-2 leading-relaxed">
                We will email a verification code to{' '}
                <span className="font-semibold text-gray-700">{email || '(not set)'}</span> so we can confirm it is you before
                you set a new password.
              </p>
              {sendError && (
                <p className="text-red-600 text-xs mt-3 text-left w-full bg-red-50 border border-red-100 rounded-md px-2.5 py-2">
                  {sendError}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleDisagree}
                disabled={sending}
                className="flex-1 h-8 rounded-md border border-gray-200 text-gray-600 font-medium text-xs cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Decline
              </button>
              <button
                type="button"
                onClick={() => void handleAgree()}
                disabled={sending || !email}
                className="flex-1 h-8 rounded-md bg-green-500 text-white font-medium text-xs cursor-pointer whitespace-nowrap hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {sending ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-sm" />
                    Sending…
                  </>
                ) : (
                  'Agree and send'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
